<template>
  <div>
    <div class='tab-buttons'>
      <rp-button :class="{selected: isStandard}" type='secondary' @click="onClickStandard">RayPRO Standard</rp-button>
      <rp-button :class="{selected: !isStandard}" type='secondary' @click="onClickClinical">RayPRO Clinical</rp-button>
    </div>
    <h3 class="title">Informed consent</h3>
    <div v-if="isStandard" class="standard">
      <div class='consent bold'>
        Please read the statement below to the patient and receive oral or written consent before any data is entered
        into this form.
      </div>
      <div class='consent'>
        “RayPRO is an online system that gathers feedback on your vision and surgery satisfaction by sending up to six
        short questionnaires over three years. Responses to the questions are confidential. Your surgeon
        and Rayner will only have access to aggregated data for the purpose of continually improving patient safety and
        satisfaction, clinical research, and for future product and service development.”
      </div>
      <div class='consent bold'>
        “Do you consent to your email address, basic surgery details, surgical outcomes and satisfaction questionnaire
        responses being added to the RayPRO system to allow the data controller, Rayner Intraocular Lenses Limited, to
        send and process questionnaires in the future?”
      </div>
      <div class='consent'>
        “You can refuse or withdraw your consent at any time, without detriment or impact to the services you receive, by
        writing to <a href='mailto:raypro@rayner.com'>raypro@rayner.com</a>. If you would like more information on
        Rayner’s processing activity, please visit
        <a href='https://rayner.com/wp-content/uploads/2023/10/RAYP230376_RayPRO-Patient-Privacy-Leaflet-08-23-EN-site.pdf'
        target='_blank' rel='noopener'>RAYP230376_RayPRO-Patient-Privacy-Leaflet-08-23-EN-site.pdf (rayner.com)</a>”
        (the link is also on the RayPRO Patient Leaflet).
      </div>
    </div>
    <div v-if="!isStandard" class="clinical">
      <div class='consent bold'>
        If <span class='underline'>oral consent</span> is obtained, please read the statement below to the patient and
        confirm that the patient has provided oral consent by ticking the relevant checkbox on the RayPRO online form
        before any data is entered into RayPRO/RayPRO Clinical
        (<a href='https://raypro.rayner.com/' target='_blank' rel='noopener'>https://raypro.rayner.com/</a>)
      </div>
      <div class='consent indent'>
        “RayPRO Clinical is an online system that gathers feedback on your vision and surgery satisfaction by sending
        up to six short questionnaires over three years. RayPRO Clinical has been developed by Rayner Intraocular
        Lenses Limited (Rayner) in order to evaluate outcomes for its intraocular lens products.
      </div>
      <div class='consent indent'>
        If you choose to provide consent to receiving questionnaires, the following information containing your
        personal data may be uploaded to RayPRO Clinical:
        <ol type='i'>
          <li>your email address;</li>
          <li>ID assigned in clinic;</li>
          <li>basic surgery details; and</li>
          <li>questionnaire responses.</li>
        </ol>
      </div>
      <div class='consent indent'>
        When your personal data is uploaded to RayPRO Clinical it is ‘pseudonymised’. This means that it is replaced
        by an artificial identifier or ‘pseudonym’.
      </div>
      <div class='consent indent'>
        Rayner will be the ‘data controller’ of the pseudonymised personal data uploaded to RayPRO Clinical. This means
        it is responsible for determining how it is used. Your hospital and/or your surgeon may also be considered to
        be a ‘data controller’ of personal data uploaded to RayPRO Clinical. Your pseudonymised personal data may be
        processed by your surgeon, hospital and Rayner for the purposes of continued evaluation of patient safety,
        satisfaction, clinical research and for future product and service development.
      </div>
      <div class='consent indent'>
        Your pseudonymised personal data may also be processed by third-party statisticians appointed by Rayner who
        will only use the pseudonymised personal data in accordance with Rayner’s instructions.
      </div>
      <div class='consent indent'>
        Neither Rayner nor any third-party statistician will have any ability to re-identify your pseudonymised
        personal data. This means for example that they will be unable to identify that any questionnaire responses you
        provide relate to you personally.
      </div>
      <div class='consent indent'>
        You can refuse or withdraw your consent at any time, without detriment or impact to the services you receive, by
        writing to <a href='mailto:raypro@rayner.com'>raypro@rayner.com</a>. If you would like more information on how
        Rayner handles your personal data please visit
        <a href='https://rayner.com/wp-content/uploads/2023/10/RAYP230376_RayPRO-Patient-Privacy-Leaflet-08-23-EN-site.pdf'
        target='_blank' rel='noopener'>RAYP230376_RayPRO-Patient-Privacy-Leaflet-08-23-EN-site.pdf (rayner.com)</a> and
        <a href='https://rayner.com/privacy-policy/' target='_blank' rel='noopener'>https://rayner.com/privacy-policy/</a>.
      </div>
      <div class='consent bold indent'>
        You (patient) consent to your email address, ID assigned in clinic, basic surgery details and questionnaire
        responses being added to the RayPRO Clinical system to allow Rayner Intraocular Lenses Limited, your surgeon
        and hospital to include your details on RayPRO Clinical and send and process questionnaires in the future. Do
        you consent?”
      </div>
      <div class='consent bold'>
        If <span class='underline'>written consent</span> is obtained, please provide a printed copy of this statement
        to the patient and ensure that they have consented by ticking the relevant check-box below and signed before
        any data is entered into RayPRO/RayPRO Clinical.
      </div>
      <div class='consent bold indent'>
        I consent to my email address, ID assigned in clinic, basic surgery details and questionnaire responses being
        added to the RayPRO Clinical system to allow Rayner Intraocular Lenses Limited, my surgeon and hospital to
        include my details on RayPRO Clinical and send and process questionnaires in the future ☐ 
      </div>
      <div class='consent bold indent sigblock'>
        <div class='sigvalue'>Patient name</div>
        <div class='sigvalue'>Patient signature</div>
        <div class='sigvalue'>Date</div>
      </div>
    </div>
  </div>
</template>

<script>
  import Button from './inputs/Button'

  export default {

    components: {
      'rp-button': Button,
    },

    data() {
      return {
        isStandard: true,
      }
    },

    methods: {
      onClickStandard() {
        this.isStandard = true
      },

      onClickClinical() {
        this.isStandard = false
      },
    }
  }
</script>

<style lang="sass" scoped>
  .title
    text-transform: none

  .tab-buttons
    display: flex
    gap: 20px

  .consent
    font-size: larger
    font-weight: normal
    margin-bottom: 10px
    &.bold
      font-weight: bold
    &.indent
      margin-left: 10px
    .underline
      text-decoration: underline
    a
      font-weight: bold
      &:hover
        text-decoration: underline

  .sigblock
    display: flex
    .sigvalue
      border: 1px solid
      padding: 0 0 25px 5px
      flex-grow: 1
</style>


// WEBPACK FOOTER //
// ../raypro_ui/src/components/ui/ConsentStatement.vue